import { useMemo } from 'react';

import { NetworkStrip } from './NetworkStrip.js';
import { CyberMap, type CyberLink } from '../maps/CyberMap.js';
import { LayerPanel } from '../maps/LayerPanel.js';
import type { MapContextKind, MapModifierClick } from '../maps/RegionMap.js';
import {
  applyPick,
  naturalSlotFor,
  pickRowsFor,
  targetForSubject,
  type PickRow,
} from '../model/builder.js';
import { networkRows } from '../model/cyber.js';
import { armedCount, filterRoster } from '../model/filter.js';
import { CYBER_LAYER_LABELS } from '../model/map-layers.js';
import { missionLabel, paramSpecOf } from '../model/missions.js';
import { toDrafts } from '../model/queue.js';
import { rosterOf } from '../model/roster.js';
import type { ScenarioModel } from '../model/scenario.js';
import { useConsoleStore } from '../state/store.js';
import type { CellViewWire } from '@badger-sentinel/protocol';

/**
 * The cyber board — the design's `taskableView` for `CYBER` (markup 352–353).
 *
 * Same pane furniture as the other two boards, because the design has one: a
 * canvas region with three mutually exclusive `x-import`s in it and one LAYERS
 * control above them (markup 343–370). So `maps/LayerPanel.tsx` is at its
 * **third** caller and needed no change, and the pane's own frame is **forked**
 * again — see below, because M5-D3 asks the question a third time and this is
 * where it stops being answered for free.
 *
 * ## What is on this canvas, and what is deliberately not
 *
 * The friendly half is `view.friendly[]`, every asset hanging off the enclave
 * its `network` names (M5 F4). The enemy half is `view.cyber.contacts[]` and
 * **nothing else** — not `view.contacts[]`, which is the detection picture.
 * `model/cyber.ts` keeps those apart structurally rather than by a filter, and
 * `model/scenario.ts` never puts a cyber-revealed machine in `UNITS`, which is
 * what the other two canvases draw from.
 *
 * ## The gestures end where the same gestures from the rail end
 *
 * A card click and a roster row click both call `select`. ⇧-click fills the slot
 * `naturalSlotFor` picks, ⌥ forces the target slot, ⌘ collects into the batch.
 * A right-click opens the menu the shell owns. None of that is re-decided here.
 *
 * The one gesture this board has that neither other one does is the **tasking
 * vector**: `api.setLink` draws an arc from the actor to the target with the
 * mission named on it, so the order being composed is visible on the picture it
 * acts on. It is fed from the *builder draft*, not from the queue — a queued
 * order is already listed twice (the dock and the rail) and a canvas full of
 * arcs would be a third list rather than a picture.
 */

export const CYBER_MIN_HEIGHT = 420;

export interface CyberViewProps {
  readonly view: CellViewWire;
  readonly model: ScenarioModel;
  /** The `unit` / `network` / `canvas` menus, which the shell owns and routes. */
  readonly onContextMenu: (
    subject: 'unit' | 'network' | 'canvas',
    id: string,
    x: number,
    y: number,
  ) => void;
}

const CONTEXT_SUBJECT: Readonly<Record<MapContextKind, 'unit' | 'network' | 'canvas'>> =
  Object.freeze(
    // `region` on this canvas is a segment **title bar**, and by the time it
    // reaches here `maps/CyberMap.tsx` has already turned the router the event
    // carried into the enclave it heads. Third vocabulary, one event.
    { unit: 'unit', region: 'network', empty: 'canvas' },
  );

export function CyberView(props: CyberViewProps): JSX.Element {
  const view = props.view;

  const selection = useConsoleStore((state) => state.selection);
  const select = useConsoleStore((state) => state.select);
  const layers = useConsoleStore((state) => state.cyberLayers);
  const toggleCyberLayer = useConsoleStore((state) => state.toggleCyberLayer);
  const panelOpen = useConsoleStore((state) => state.mapLayerPanelOpen);
  const setPanelOpen = useConsoleStore((state) => state.setMapLayerPanelOpen);
  const hoverId = useConsoleStore((state) => state.hoverId);
  const setHover = useConsoleStore((state) => state.setHover);
  const command = useConsoleStore((state) => state.mapCommand);
  const clearMapCommand = useConsoleStore((state) => state.clearMapCommand);

  const search = useConsoleStore((state) => state.search);
  const facets = useConsoleStore((state) => state.facets);
  const queue = useConsoleStore((state) => state.queue);
  const builder = useConsoleStore((state) => state.builder);
  const armedSlot = useConsoleStore((state) => state.armedSlot);
  const fillSlot = useConsoleStore((state) => state.fillSlot);
  const fillMission = useConsoleStore((state) => state.fillMission);
  const fillTarget = useConsoleStore((state) => state.fillTarget);
  const fillParam = useConsoleStore((state) => state.fillParam);
  const toggleBatch = useConsoleStore((state) => state.toggleBatch);
  const showToast = useConsoleStore((state) => state.showToast);

  /**
   * What the roster filter matched, or `null`.
   *
   * The union is this board's own and it is not decoration: the roster is built
   * from `friendly[]` and `contacts[]`, so a machine that exists **only** in
   * `cyber.contacts[]` is in no roster group and would be dimmed by every search
   * — including one that names it. So the aliases the search text matches are
   * added back. Nothing is added that the search did not match, and nothing is
   * *hidden* either way: `setDim` dims, it never removes.
   */
  const dim = useMemo<readonly string[] | null>(() => {
    if (search === '' && armedCount(facets) === 0) return null;
    const roster = rosterOf(view, toDrafts(queue));
    const matched = filterRoster(roster, search, facets).flatMap((group) =>
      group.rows.map((row) => row.id),
    );
    const needle = search.trim().toLowerCase();
    const cyber =
      needle === ''
        ? []
        : view.cyber.contacts
            .filter(
              (contact) =>
                contact.alias.toLowerCase().includes(needle) ||
                contact.network.toLowerCase().includes(needle),
            )
            .map((contact) => contact.alias);
    return [...new Set([...matched, ...cyber])];
  }, [view, queue, search, facets]);

  const pickRows = useMemo<readonly PickRow[]>(
    () =>
      armedSlot === 'asset' || armedSlot === 'target' ? pickRowsFor(view, builder, armedSlot) : [],
    [view, builder, armedSlot],
  );
  const pick = useMemo(
    () =>
      armedSlot !== null && pickRows.length > 0
        ? { slot: armedSlot, ids: pickRows.map((row) => row.id) }
        : null,
    [armedSlot, pickRows],
  );

  const strip = useMemo(() => networkRows(view), [view]);

  /**
   * The vector the canvas draws, straight off the builder draft.
   *
   * Both ends have to be on this board or the renderer finds no node and draws
   * nothing, which is the honest outcome — an `ENGAGE` on a satellite has no arc
   * here because it has nothing to do with this picture. The mission is `null`
   * until one is chosen, and the renderer draws the arc without a label rather
   * than with an empty chip.
   */
  const link = useMemo<CyberLink | null>(() => {
    const actorId = builder.asset;
    const target = builder.target;
    if (actorId === null || target === null) return null;
    const targetId =
      target.kind === 'OWN_ASSET'
        ? target.asset
        : target.kind === 'CONTACT'
          ? target.contact
          : null;
    if (targetId === null) return null;
    return {
      actorId,
      targetId,
      mission: builder.mission === null ? null : missionLabel(builder.mission),
    };
  }, [builder]);

  const takeRow = (row: PickRow): void => {
    applyPick(row, {
      asset: (id) => fillSlot('asset', id),
      mission: fillMission,
      target: fillTarget,
      param: (value) => {
        const spec = builder.mission === null ? null : paramSpecOf(builder.mission);
        if (spec !== null) fillParam(spec.key, value);
      },
    });
  };

  /** ⇧ fills the natural slot, ⌥ forces the target slot, ⌘ collects into the batch. */
  const onModClick = (detail: MapModifierClick): void => {
    if (detail.meta) {
      toggleBatch(detail.id);
      return;
    }
    // An enclave is never a wire target (`targetForSubject` has no arm for one) —
    // it is the one subject on this board `subjectOf` resolves to `NETWORK`
    // rather than an asset or a contact. A modifier-click on one is inert rather
    // than a rejection the operator has to read and dismiss under the clock; a
    // bare click still selects it, same as any other card.
    if (subjectOf(view, detail.id)?.kind === 'NETWORK') return;
    const slot = detail.alt ? 'target' : detail.shift ? naturalSlotFor(view, detail.id) : null;
    if (slot === null) return;
    if (slot === 'asset') {
      fillSlot('asset', detail.id);
      showToast('ASSET ← ' + detail.id);
      return;
    }
    const target = targetForSubject(view, detail.id);
    if (target === null) {
      showToast(detail.id + ' CANNOT BE A TARGET', 'warn');
      return;
    }
    fillTarget(target);
    showToast('TARGET ← ' + detail.id);
  };

  return (
    <div
      data-testid="cyber-view"
      style={{
        position: 'relative',
        // `1 1 auto` with a floor, for the reason `MwView`, `TheatreView` and
        // `SdaView` all state — and which Phase 9 restated when it retired the
        // harness this originally guarded against: the floor is what keeps a short
        // viewport from collapsing the board.
        flex: '1 1 auto',
        minHeight: CYBER_MIN_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--canvas)',
      }}
    >
      <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
        <CyberMap
          model={props.model}
          layers={layers}
          selectedUnit={
            selection !== null && (selection.kind === 'ASSET' || selection.kind === 'CONTACT')
              ? selection.id
              : null
          }
          dim={dim}
          pick={pick}
          hover={hoverId}
          link={link}
          command={command}
          onSelect={(id) => select(id === null ? null : subjectOf(view, id))}
          onNetwork={(network) => {
            if (network !== null) select({ kind: 'NETWORK', id: network });
          }}
          onPick={(slot, id) => {
            const row = pickRows.find(
              (candidate) => candidate.slot === slot && candidate.id === id,
            );
            if (row !== undefined) takeRow(row);
          }}
          onContext={(kind, id, x, y) => props.onContextMenu(CONTEXT_SUBJECT[kind], id, x, y)}
          onHover={setHover}
          onModClick={onModClick}
          onCommandApplied={clearMapCommand}
        />
        <LayerPanel
          labels={CYBER_LAYER_LABELS}
          layers={layers}
          open={panelOpen}
          onToggleOpen={() => setPanelOpen(!panelOpen)}
          onToggle={toggleCyberLayer}
        />
      </div>
      <NetworkStrip
        rows={strip}
        selected={selection?.kind === 'NETWORK' ? selection.id : null}
        onSelect={select}
        onContextMenu={(id, x, y) => props.onContextMenu('network', id, x, y)}
      />
    </div>
  );
}

/**
 * A card click names an id; the view says what it is.
 *
 * The third arm is the difference from `SdaView`'s: a cyber-revealed machine is
 * **not** in `view.contacts[]` and never will be (RB §5.6.2 — "visible (to
 * cyber; not FOUND)"), so a board that only asked the detection picture would
 * find nothing and clear the selection out from under the operator's click.
 * It still resolves as a `CONTACT` selection, because that is what it is: an
 * alias for something the seat was never told the identity of.
 */
function subjectOf(
  view: CellViewWire,
  id: string,
): { readonly kind: 'ASSET' | 'CONTACT' | 'NETWORK'; readonly id: string } | null {
  if (view.contacts.some((contact) => contact.id === id)) return { kind: 'CONTACT', id };
  if (view.friendly.some((asset) => asset.id === id)) return { kind: 'ASSET', id };
  if (view.cyber.contacts.some((contact) => contact.alias === id)) return { kind: 'CONTACT', id };
  // The stand-in key an unnamed router card carries is the enclave's own id, so
  // clicking it selects the enclave — the only thing there is to select.
  if (view.cyber.networks.some((network) => network.id === id)) return { kind: 'NETWORK', id };
  return null;
}
